import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Bell, ChevronDown, HelpCircle, LogOut, Menu, Search, Settings, ShieldCheck, User } from "lucide-react";
import type { RootState } from "../../store/store";
import NotificationBell from "../../pages/Notification/NotificationBell";
import { TierBadge } from "../TierBadge";
import { TierBenefitsModal } from "../TierBenefits";
import { useTier } from "../../hooks/useTier";
import { useLogout } from "../../hooks/useLogout";
import ProfilePicture from "../ProfilePicture";
import { parseSearchInput } from "../Listings/searchUtils";
import { Brand } from "../ui/Brand";
import { Button } from "../ui";
import { cn } from "../../lib/utils";

interface HeaderProps {
  userName?: string;
  userAvatar?: string;
  /** Opens the Help & Support panel */
  handleClick?: () => void;
  /** Opens the mobile navigation drawer */
  onMenuClick?: () => void;
}

/** Search box that routes to Discover with city / state / ZIP filters */
const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");

  // Mirror the current Discover search so the box never looks out of sync
  useEffect(() => {
    if (pathname === "/buy") {
      setValue(params.get("city") || params.get("zip") || params.get("state") || "");
    }
  }, [pathname, params]);

  // "/" focuses search from anywhere, like most pro tools
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) && !el.isContentEditable) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseSearchInput(value);
    const next = new URLSearchParams();
    if (parsed.city) next.set("city", parsed.city);
    if (parsed.state) next.set("state", parsed.state);
    if (parsed.zip) next.set("zip", parsed.zip);
    navigate(`/buy${next.toString() ? `?${next}` : ""}`);
    inputRef.current?.blur();
  };

  return (
    <form onSubmit={submit} role="search" className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search by city, state or ZIP"
        aria-label="Search properties"
        className="h-10 w-full rounded-xl bg-gray-100/80 pl-9 pr-10 text-sm text-gray-900 ring-1 ring-transparent transition-all placeholder:text-gray-500 hover:bg-gray-100 focus:bg-white focus:outline-none focus:ring-gray-300 focus:shadow-[0_0_0_4px_rgb(17_24_39/0.05)]"
      />
      <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded-md bg-white px-1.5 py-0.5 font-sans text-xs font-medium text-gray-400 ring-1 ring-gray-200 sm:block">
        /
      </kbd>
    </form>
  );
};

const UserMenu: React.FC<{ displayName: string; avatar?: string; email?: string | null; onHelp?: () => void }> = ({
  displayName,
  avatar,
  email,
  onHelp,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { logout } = useLogout();
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const itemClass =
    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900 [&_svg]:size-4 [&_svg]:text-gray-400";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-gray-100"
      >
        <ProfilePicture photoUrl={avatar} name={displayName} size="sm" />
        <span className="hidden max-w-[140px] truncate text-sm font-medium text-gray-900 md:inline">{displayName}</span>
        <ChevronDown className={cn("hidden size-4 text-gray-400 transition-transform md:block", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 origin-top-right animate-dialog-in rounded-xl bg-white p-1.5 shadow-overlay ring-1 ring-gray-950/5"
        >
          <div className="px-2.5 pb-2.5 pt-2">
            <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
            {email && <p className="truncate text-xs text-gray-500">{email}</p>}
          </div>
          <div className="my-1 h-px bg-gray-100" />
          <Link to="/profile" role="menuitem" className={itemClass}>
            <User /> Profile
          </Link>
          <Link to="/profile/settings/notifications" role="menuitem" className={itemClass}>
            <Bell /> Notification settings
          </Link>
          <Link to="/profile/verification" role="menuitem" className={itemClass}>
            <Settings /> Verification
          </Link>
          {onHelp && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onHelp();
              }}
              className={itemClass}
            >
              <HelpCircle /> Help & support
            </button>
          )}
          <div className="my-1 h-px bg-gray-100" />
          <button type="button" role="menuitem" onClick={() => void logout()} className={itemClass}>
            <LogOut /> Log out
          </button>
        </div>
      )}
    </div>
  );
};

const WelcomeHeader: React.FC<HeaderProps> = ({ userName, userAvatar, handleClick, onMenuClick }) => {
  const { tier, isLoading, profile } = useTier();
  const [isTierModalOpen, setIsTierModalOpen] = useState(false);

  const firstName = useSelector((state: RootState) => state.auth.firstName);
  const lastName = useSelector((state: RootState) => state.auth.lastName);
  const email = useSelector((state: RootState) => state.auth.email);
  const { pathname } = useLocation();
  // /profile shows the verification panel and /profile/verification is the flow itself
  const onVerificationSurface = pathname === "/profile" || pathname.startsWith("/profile/verification");

  const displayName =
    userName ||
    (firstName && lastName ? `${firstName} ${lastName}` : null) ||
    (profile ? `${profile.firstName} ${profile.lastName}` : "User");

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-gray-200 bg-white/85 px-4 backdrop-blur-md sm:px-6">
      {/* Mobile: menu + brand */}
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Open navigation"
        className="-ml-1 rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 lg:hidden"
      >
        <Menu className="size-5" />
      </button>
      <Brand to="/user" compact className="sm:hidden" />
      <Brand to="/user" className="hidden sm:inline-flex lg:hidden" />

      <div className="hidden flex-1 md:flex">
        <GlobalSearch />
      </div>
      <div className="flex-1 md:hidden" />

      <div className="flex items-center gap-1.5 sm:gap-2">
        {/* Unverified members get the next step, not a negative status pill; verified tiers show their badge.
            It stays neutral so the page's own CTA is the only orange action, and it is hidden on the
            profile and verification pages, which carry the verification panel themselves. */}
        {!isLoading && profile?.emailVerified && tier <= 1 && !onVerificationSurface && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            leftIcon={<ShieldCheck />}
            className="hidden text-gray-700 sm:inline-flex"
          >
            <Link to="/profile/verification">Get verified</Link>
          </Button>
        )}
        {!isLoading && profile?.emailVerified && tier >= 2 && (
          <button
            type="button"
            onClick={() => setIsTierModalOpen(true)}
            className="hidden rounded-full transition-opacity hover:opacity-80 sm:block"
            aria-label="View tier information"
          >
            <TierBadge tier={tier as 1 | 2 | 3 | 4} size="small" />
          </button>
        )}
        <NotificationBell variant="icon" />
        <div className="mx-1 hidden h-6 w-px bg-gray-200 sm:block" />
        <UserMenu displayName={displayName} avatar={userAvatar} email={email} onHelp={handleClick} />
      </div>

      <TierBenefitsModal
        isOpen={isTierModalOpen}
        onClose={() => setIsTierModalOpen(false)}
        currentTier={tier as 1 | 2 | 3 | 4}
      />
    </header>
  );
};

export default WelcomeHeader;
